import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getProducts } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { useCustomerViewMode } from "../components/B2BMode";
import { useCategories, useThemeSettings, useCategoryBrands, useParamValues, useBestsellers, useBrandConfigs } from "../lib/useCache";

const DEFAULT_LIMIT = 24;

export function useCategoryPage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const searchQ = searchParams.get("q") || "";
    const brandFromUrl = searchParams.get("brand") || "";

    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [transitioning, setTransitioning] = useState(false);
    const [currentCat, setCurrentCat] = useState(null);
    const [selectedParams, setSelectedParams] = useState({});
    const prevSlugRef = useRef(slug);

    const [page, setPage] = useState(1);
    const [sort, setSort] = useState("created_at_desc");
    const [selectedBrands, setSelectedBrands] = useState(brandFromUrl ? [brandFromUrl] : []);
    const [inStockOnly, setInStockOnly] = useState(false);
    const [onSaleOnly, setOnSaleOnly] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 200000]);
    const [search, setSearch] = useState(searchQ);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem("sg_view_mode") || "grid");

    useEffect(() => {
        const token = localStorage.getItem("sg_customer_token");
        if (token) {
            api.get("/customer/preferences", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => {
                    const saved = r.data?.view_mode;
                    if (saved && ["grid", "list", "b2b"].includes(saved)) {
                        setViewMode(saved);
                        localStorage.setItem("sg_view_mode", saved);
                    }
                }).catch(() => {});
        }
    }, []);

    const handleViewModeChange = useCallback((mode) => {
        setViewMode(mode);
        localStorage.setItem("sg_view_mode", mode);
        const token = localStorage.getItem("sg_customer_token");
        if (token) {
            api.patch("/customer/preferences", { view_mode: mode }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        }
    }, []);

    const [b2bMode] = useCustomerViewMode();
    const [pageLimit, setPageLimit] = useState(DEFAULT_LIMIT);
    const [promoCards, setPromoCards] = useState([]);
    const [promoFrequency, setPromoFrequency] = useState(5);

    const { data: categories = [] } = useCategories(true);
    const { data: themeData } = useThemeSettings();
    const globalDisplayStyle = themeData?.category_display_style || "pills";
    const { data: catBrands = [] } = useCategoryBrands(slug);
    const { data: paramOptions = [] } = useParamValues(slug);
    const { data: bestsellers = [] } = useBestsellers(slug, 4);
    const { data: brandConfigs = [] } = useBrandConfigs();

    const isLoggedIn = !!localStorage.getItem("sg_customer_token");

    // Saved filters
    const [savedFilters, setSavedFilters] = useState([]);
    const [saveFilterName, setSaveFilterName] = useState("");
    const [showSaveFilter, setShowSaveFilter] = useState(false);

    useEffect(() => {
        if (isLoggedIn) {
            api.get("/customer/saved-filters").then(r => setSavedFilters(r.data || [])).catch(() => {});
        }
    }, [isLoggedIn]);

    const handleSaveFilter = useCallback(async () => {
        if (!saveFilterName.trim()) { toast.error("Zadejte nazev filtru"); return; }
        try {
            const filters = { category_slug: slug || "", search: search || "", brands: selectedBrands, in_stock: inStockOnly, on_sale: onSaleOnly, price_range: priceRange, params: selectedParams, sort };
            const res = await api.post("/customer/saved-filters", { name: saveFilterName, filters });
            setSavedFilters(prev => [res.data, ...prev]);
            setSaveFilterName("");
            setShowSaveFilter(false);
            toast.success("Filtr ulozen");
        } catch (e) { toast.error(e.response?.data?.detail || "Chyba"); }
    }, [saveFilterName, slug, search, selectedBrands, inStockOnly, onSaleOnly, priceRange, selectedParams, sort]);

    const handleLoadFilter = useCallback((f) => {
        const fl = f.filters || {};
        setSelectedBrands(fl.brands || []);
        setInStockOnly(fl.in_stock || false);
        setOnSaleOnly(fl.on_sale || false);
        setPriceRange(fl.price_range || [0, 200000]);
        setSearch(fl.search || "");
        setSelectedParams(fl.params || {});
        if (fl.sort) setSort(fl.sort);
        toast.success(`Filtr "${f.name}" nacten`);
    }, []);

    const handleDeleteFilter = useCallback(async (id) => {
        try { await api.delete(`/customer/saved-filters/${id}`); setSavedFilters(prev => prev.filter(f => f.id !== id)); toast.success("Filtr smazan"); }
        catch { toast.error("Chyba"); }
    }, []);

    // Category resolution
    useEffect(() => {
        setCurrentCat(categories.find(c => c.slug === slug) || null);
        if (prevSlugRef.current !== slug) {
            setTransitioning(true); setPage(1); setProducts([]); setSearch(""); setSelectedParams({});
            setTimeout(() => setTransitioning(false), 150);
            prevSlugRef.current = slug;
        }
    }, [slug, categories]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setSearch(searchQ); }, [searchQ]);

    // Promo cards
    useEffect(() => {
        const catId = currentCat?.id || "";
        api.get(`/promo-cards/for-listing${catId ? `?category_id=${catId}` : ""}`)
            .then(r => { const d = r.data || {}; setPromoCards(d.cards || []); setPromoFrequency(d.settings?.frequency || 5); })
            .catch(() => {});
    }, [currentCat, slug]);

    const paramFilterStr = useMemo(() => {
        const parts = [];
        Object.entries(selectedParams).forEach(([k, v]) => { if (v) parts.push(`${k}:${v}`); });
        return parts.join(",") || undefined;
    }, [selectedParams]);

    // Product loading
    const loadProducts = useCallback((pageNum, append = false) => {
        if (!append) setLoading(true); else setLoadingMore(true);
        const params = {
            page: pageNum, limit: pageLimit, sort, is_active: true,
            ...(slug ? { category_slug: slug } : {}),
            ...(selectedBrands.length ? { brands: selectedBrands.join(",") } : {}),
            ...(inStockOnly ? { in_stock: true } : {}),
            ...(onSaleOnly ? { on_sale: true } : {}),
            ...(search ? { search } : {}),
            min_price: priceRange[0] || undefined,
            max_price: priceRange[1] < 200000 ? priceRange[1] : undefined,
            param_filters: paramFilterStr,
        };
        getProducts(params).then(r => {
            const items = r.data?.items || [];
            const tot = r.data?.total || 0;
            const pgs = r.data?.pages || 1;
            if (append) setProducts(prev => [...prev, ...items]);
            else { setProducts(items); window.scrollTo({ top: 0, behavior: "smooth" }); }
            setTotal(tot);
            setHasMore(pageNum < pgs);
        }).catch(() => { if (!append) setProducts([]); })
          .finally(() => { setLoading(false); setLoadingMore(false); });
    }, [slug, sort, selectedBrands, inStockOnly, onSaleOnly, search, priceRange, paramFilterStr, pageLimit]);

    useEffect(() => {
        setPage(1); loadProducts(1, false);
    }, [slug, sort, selectedBrands, inStockOnly, onSaleOnly, search, priceRange, paramFilterStr, pageLimit]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadMore = useCallback(() => { const n = page + 1; setPage(n); loadProducts(n, true); }, [page, loadProducts]);
    const toggleBrand = useCallback((b) => setSelectedBrands(prev => prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b]), []);
    const toggleParam = useCallback((key, val) => setSelectedParams(prev => ({ ...prev, [key]: prev[key] === val ? "" : val })), []);
    const clearFilters = useCallback(() => { setSelectedBrands([]); setInStockOnly(false); setOnSaleOnly(false); setPriceRange([0, 200000]); setSearch(""); setSelectedParams({}); }, []);

    const activeParamCount = Object.values(selectedParams).filter(Boolean).length;
    const hasFilters = selectedBrands.length > 0 || inStockOnly || onSaleOnly || search || priceRange[0] > 0 || priceRange[1] < 200000 || activeParamCount > 0;

    const breadcrumb = useMemo(() => {
        if (!currentCat) return [];
        const chain = [];
        let c = currentCat;
        while (c.parent_id) { const p = categories.find(x => x.id === c.parent_id); if (!p) break; chain.unshift(p); c = p; }
        return [...chain, currentCat];
    }, [currentCat, categories]);

    const isRootCategory = currentCat && !currentCat.parent_id;
    const subcategoryChildren = useMemo(() => {
        if (!currentCat || !isRootCategory) return [];
        return categories.filter(c => c.parent_id === currentCat.id);
    }, [currentCat, isRootCategory, categories]);

    const categoryDisplayStyle = currentCat?.display_style || globalDisplayStyle;

    return {
        slug, searchQ, products, total, hasMore, loadingMore, loading, transitioning,
        currentCat, page, sort, setSort, selectedBrands, selectedParams, inStockOnly, setInStockOnly,
        onSaleOnly, setOnSaleOnly, priceRange, setPriceRange, search, setSearch,
        filtersOpen, setFiltersOpen, viewMode, handleViewModeChange,
        pageLimit, setPageLimit, setPage, promoCards, promoFrequency,
        categories, catBrands, paramOptions, bestsellers, brandConfigs, isLoggedIn,
        savedFilters, saveFilterName, setSaveFilterName, showSaveFilter, setShowSaveFilter,
        handleSaveFilter, handleLoadFilter, handleDeleteFilter,
        handleLoadMore, toggleBrand, toggleParam, clearFilters,
        activeParamCount, hasFilters, breadcrumb, isRootCategory,
        subcategoryChildren, categoryDisplayStyle,
    };
}
